import axios from 'axios';

// Define the base URL for the API. Adjust if your server runs elsewhere.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Server base URL (without /api suffix) for static assets
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

// Debug logging for API URL
console.log('🔧 API Service Configuration:');
console.log('   REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('   API_BASE_URL:', API_BASE_URL);
console.log('   SERVER_BASE_URL:', SERVER_BASE_URL);
console.log('   Environment:', process.env.NODE_ENV);

/**
 * Get the full URL for a static asset (e.g., uploaded images)
 * Handles both relative paths (local dev) and absolute URLs (GCS in production)
 * @param {string} url - The URL or path to the asset
 * @returns {string} - The full URL to the asset
 */
export const getStaticAssetUrl = (url) => {
  if (!url) return null;

  // If it's already an absolute URL (e.g., GCS URL), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // If it's a relative path, prepend the server base URL
  return `${SERVER_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Create an axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes timeout for long operations like simulations
  // headers: { // Remove default Content-Type
  //   'Content-Type': 'application/json',
  // },
});

// Export the raw axios instance for advanced use cases (e.g., custom responseType)
export { apiClient as rawApiClient };

// Function to set the auth token for subsequent requests
const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['x-auth-token'] = token;
    console.log('Auth token set in axios defaults');
  } else {
    delete apiClient.defaults.headers.common['x-auth-token'];
    console.log('Auth token removed from axios defaults');
  }
};


// --- Onboarding API Calls ---

/**
 * @deprecated When useUnifiedMessageEndpoint is enabled, non-initial onboarding messages
 * are routed through sendMessage() instead. This function is still used for the initial
 * greeting (empty message '') and as a fallback when the flag is off.
 */
export const sendOnboardingMessage = async (message, history, conversationId = null, invitationToken = null, options = {}) => {
  try {
    // Add diagnostic logging
    console.log("API CALL - sendOnboardingMessage with params:", {
      messageLength: message?.length || 0,
      historyCount: history?.length || 0,
      conversationId: conversationId || "NULL",
      hasInvitationToken: !!invitationToken
    });
    console.log("Full payload:", { message, history, conversationId, invitationToken: invitationToken ? 'present' : 'none' });

    // Assumes token is set globally via setAuthToken before calling
    const payload = { message, history };

    // Include conversationId if it exists (after initial call)
    if (conversationId) {
      payload.conversationId = conversationId;
      console.log("Added conversationId to payload:", conversationId);
    }

    // Include invitationToken if provided (for flow resolution)
    if (invitationToken) {
      payload.invitationToken = invitationToken;
      console.log("Added invitationToken to payload");
    }

    // Include isInternal flag (for system-triggered messages that shouldn't appear in transcript)
    if (options.isInternal) {
      payload.isInternal = true;
    }

    // Include inputMethod if provided (voice vs text tracking)
    if (options.inputMethod) {
      payload.inputMethod = options.inputMethod;
    }

    const response = await apiClient.post('/onboarding/message', payload);
    console.log("API RESPONSE - sendOnboardingMessage:", {
      hasResponse: !!response.data.response,
      hasConversationId: !!response.data.conversationId,
      conversationId: response.data.conversationId || "NOT RETURNED",
      onboardingComplete: response.data.onboardingComplete || false,
      suggestionType: response.data.suggestionType || "NONE",
      hasAssessmentDetails: !!response.data.assessmentDetails
    });
    console.log("Full API response data:", response.data);
    
    // Backend returns { response: { sender, text }, history: [...], onboardingComplete: boolean, conversationId }
    return response.data;
  } catch (error) {
    console.error('Send Onboarding Message API error:', error);
    console.error('Error details:', error.response?.data || error.message);
    console.error('Request that caused error:', { message, history, conversationId });

    // Preserve onboardingComplete flag for graceful handling of "already active session" case
    const errorData = error.response?.data || {};
    const customError = new Error(errorData.message || 'Failed to send onboarding message');
    customError.onboardingComplete = errorData.onboardingComplete || false;
    customError.errorType = errorData.error || 'unknown';
    throw customError;
  }
};

// --- Authentication API Calls ---

export const register = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register', userData);
    return response.data; // Should contain the token
  } catch (error) {
    console.error('Registration API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Registration failed');
  }
};

// This browser's device token (1 Oct 2026): lets the server recognise the device for
// "trust this device" and new-device emails. Kept across sign-outs on purpose; it grants
// nothing without the password. Storage can be unavailable, so every access is guarded.
const DEVICE_TOKEN_KEY = 'aimhi.deviceToken';
const readDeviceToken = () => { try { return localStorage.getItem(DEVICE_TOKEN_KEY) || undefined; } catch (_) { return undefined; } };
const keepDeviceToken = (data) => {
  try { if (data && typeof data.deviceToken === 'string') localStorage.setItem(DEVICE_TOKEN_KEY, data.deviceToken); } catch (_) { /* storage unavailable */ }
  return data;
};

export const login = async (credentials) => {
  try {
    const response = await apiClient.post('/auth/login', { ...credentials, deviceToken: readDeviceToken() });
    return keepDeviceToken(response.data); // Should contain the token
  } catch (error) {
    console.error('Login API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Login failed');
  }
};

// Forgot / reset password (security batch 3). The server answers forgot-password the
// same way whether or not the email has an account.
export const forgotPassword = async (email) => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Could not send the reset email. Please try again.');
  }
};

export const resetPassword = async (token, password) => {
  try {
    const response = await apiClient.post('/auth/reset-password', { token, password });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Could not reset the password. Please try again.');
  }
};

// Two-step sign-in (security batch 4): exchange the login challenge and a code for a session.
export const verifyMfa = async (mfaToken, { code, recoveryCode, rememberDevice = false }) => {
  try {
    const response = await apiClient.post('/auth/mfa/verify', { mfaToken, code, recoveryCode, rememberDevice, deviceToken: readDeviceToken() });
    return keepDeviceToken(response.data);
  } catch (error) {
    const err = new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Sign-in failed');
    err.code = error.response?.data?.code;
    throw err;
  }
};

export const getUser = async () => {
    try {
        // Assumes token is set globally via setAuthToken before calling
        const response = await apiClient.get('/auth');
        return response.data;
    } catch (error) {
        console.error('Get User API error:', error.response?.data || error.message);
        throw new Error('Failed to fetch user data');
    }
};

// --- Therapist Pathway Assignment API Calls ---
export const getPathwayAssignmentData = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/clients/${clientId}/pathway-assignment-data`, config);
    return response.data;
  } catch (error) {
    console.error('Get Pathway Assignment Data API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch pathway assignment data');
  }
};

// --- Therapist Persona Management API Calls ---
export const updateClientUiOptions = async (clientId, options, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/therapist/clients/${clientId}/ui-options`, options, config);
    return response.data;
  } catch (error) {
    console.error('Update Client UI Options API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update client UI options');
  }
};

export const updateClientAiPersona = async (clientId, personaData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/therapist/clients/${clientId}/ai-persona`, personaData, config);
    return response.data;
  } catch (error) {
    console.error('Update Client AI Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update client AI persona');
  }
};

export const startNewConversationForClient = async (clientId, personaKey, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/therapist/clients/${clientId}/start-new-conversation`, 
      { personaKey }, config);
    return response.data;
  } catch (error) {
    console.error('Start New Conversation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to start new conversation for client');
  }
};

export const updateClientAssignedPathway = async (clientId, pathwayData, token) => {
  // pathwayData: { pathwayId: string, pathwayName: string, currentPhaseName: string }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/therapist/clients/${clientId}/assigned-pathway`, pathwayData, config);
    return response.data; // Should be { msg: '...', therapySettings: {...} }
  } catch (error) {
    console.error('Update Client Assigned Pathway API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update client assigned pathway');
  }
};
// --- Chat API Calls ---

// Request Therapist Review
export const requestReview = async (conversationId, token, message = '') => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // POST request to the specific conversation's review endpoint
    const response = await apiClient.post(`/chat/${conversationId}/request-review`, { message }, config);
    return response.data; // Should be { msg: '...', status: '...', rateLimitInfo: {...} }
  } catch (error) {
    console.error('Request Review API error:', error.response?.data || error.message);
    // Re-throw with response data to preserve rate limit info
    throw error;
  }
};

// Log client-side activity events (voice/audio usage) — fire-and-forget
export const logActivityEvent = async (eventType, details = {}, conversationId = null, token = null) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    await apiClient.post('/chat/activity-event', { eventType, details, conversationId }, config);
  } catch (error) {
    // Fire-and-forget — don't throw, just warn
    console.warn('Activity event logging failed:', error.message);
  }
};

export const sendMessage = async (messageData, token) => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.post('/chat/send', messageData, config);
        // Backend now returns more info: { reply?, message?, conversationId, status, sentMessage? }
        return response.data;
    } catch (error) {
        console.error('Send Message API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error(error.response?.data?.msg || 'Failed to send message');
    }
};

export const getHistory = async (token) => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.get('/chat/history', config);
        return response.data; // Should be { messages: [...] }
    } catch (error) {
        console.error('Get History API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error('Failed to fetch chat history');
    }
};

// --- Summary API Call ---
export const generateSummary = async (conversationId, token) => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.post(`/chat/${conversationId}/summarize`, {}, config); // Empty body for POST
        return response.data; // Should be { msg: '...', summary: '...' }
    } catch (error) {
        console.error('Generate Summary API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error(error.response?.data?.msg || 'Failed to generate summary');
    }
};

// --- User Account API Calls ---
export const deleteAccount = async (token, reason = '') => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        // The optional reason for leaving travels in the DELETE body (ST-20261021-0001).
        const response = await apiClient.delete('/auth/delete', { ...config, data: { reason } });
        return response.data; // Should be { msg: '...' }
    } catch (error) {
        console.error('Delete Account API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error(error.response?.data?.msg || 'Failed to delete account');
    }
};

export const changePassword = async (passwordData, token) => {
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.post('/auth/change-password', passwordData, config);
        return response.data; // Should be { msg: 'Password changed successfully' }
    } catch (error) {
        console.error('Change Password API error:', error.response?.data || error.message);
        throw new Error(error.response?.data?.errors?.[0]?.msg || error.response?.data?.msg || 'Failed to change password');
    }
};


// --- Persona API Calls ---
export const getTherapistPersonas = async (token) => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.get('/chat/personas', config);
        return response.data; // Should be an array like [{ key: 'generalist', name: '...' }, ...]
    } catch (error) {
        console.error('Get Therapist Personas API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error('Failed to fetch therapist personas');
    }
};

// --- Simulation API Calls ---
export const getClientPersonas = async (token) => { // Accept token explicitly
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.get('/simulation/client-personas', config);
        return response.data; // Should be an array like [{ key: 'client_anxiety_mild', name: '...' }, ...]
    } catch (error) {
        console.error('Get Client Personas API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        throw new Error('Failed to fetch client personas');
    }
};

export const runSimulation = async (simulationParams, token) => { // Accept token explicitly
    try {
        const config = token ? { 
            headers: { 'x-auth-token': token },
            timeout: 900000 // 15 minutes for simulations (they can take a while)
        } : {};
        const response = await apiClient.post('/simulation/run', simulationParams, config);
        // Backend now returns { transcript, simulationId }
        return response.data;
    } catch (error) {
        console.error('Run Simulation API error:', error.response?.data || error.message);
        // Ensure error is thrown so UI can react
        // Check for timeout specifically
        if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
            throw new Error('Simulation is taking longer than expected. Please try again with fewer turns.');
        }
        throw new Error(error.response?.data?.msg || error.message || 'Failed to run simulation');
    }
};

// Get Simulation Review
export const getSimulationReview = async (simulationId, token) => { // Accept token explicitly
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const res = await apiClient.get(`/simulation/review/${simulationId}`, config);
    return res.data; // Expects { evaluation: '...', recommendations: '...' }
  } catch (err) {
    console.error('Error fetching simulation review:', err.response?.data || err.message);
    // Ensure error is thrown so UI can react
    throw err; // Re-throw to be handled by the component
  }
};

// --- Admin Persona API Calls ---

// Get All Client Personas (Admin)
export const getAdminClientPersonas = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/personas/client', config);
    return response.data; // Should be an array like [{ key: '...', name: '...', ... }, ...]
  } catch (error) {
    console.error('Get Admin Client Personas API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch client personas');
  }
};

// Add Client Persona (Admin)
export const addClientPersona = async (personaData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/personas/client', personaData, config);
    return response.data; // Should be { msg: '...', persona: {...} }
  } catch (error) {
    console.error('Add Client Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to add client persona');
  }
};

// Update Client Persona (Admin)
export const updateClientPersona = async (key, personaData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/personas/client/${key}`, personaData, config);
    return response.data; // Should be { msg: '...', persona: {...} }
  } catch (error) {
    console.error('Update Client Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update client persona');
  }
};

// Delete Client Persona (Admin)
export const deleteClientPersona = async (key, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/admin/personas/client/${key}`, config);
    return response.data; // Should be { msg: '...' }
  } catch (error) {
    console.error('Delete Client Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete client persona');
  }
};

// Get All Therapist Personas (Admin)
export const getAdminTherapistPersonas = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/personas/therapist', config);
    return response.data; // Should be an array like [{ key: '...', name: '...', ... }, ...]
  } catch (error) {
    console.error('Get Admin Therapist Personas API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch therapist personas');
  }
};

// Add Therapist Persona (Admin)
export const addTherapistPersona = async (personaData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/personas/therapist', personaData, config);
    return response.data; // Should be { msg: '...', persona: {...} }
  } catch (error) {
    console.error('Add Therapist Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to add therapist persona');
  }
};

// Update Therapist Persona (Admin)
export const updateTherapistPersona = async (key, personaData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/personas/therapist/${key}`, personaData, config);
    return response.data; // Should be { msg: '...', persona: {...} }
  } catch (error) {
    console.error('Update Therapist Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update therapist persona');
  }
};

// Delete Therapist Persona (Admin)
export const deleteTherapistPersona = async (key, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/admin/personas/therapist/${key}`, config);
    return response.data; // Should be { msg: '...' }
  } catch (error) {
    console.error('Delete Therapist Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete therapist persona');
  }
};

// Generate Reviewer Persona using AI (Admin)
export const generateReviewerPersona = async (data, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/personas/generate-reviewer', data, config);
    return response.data;
  } catch (error) {
    console.error('Generate Reviewer Persona API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to generate reviewer persona');
  }
};

// --- Admin Simulation History API Calls ---

// Get List of Simulations (Admin)
export const getSimulationsList = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/simulations', config);
    return response.data; // Should be an array of simulation summaries
  } catch (error) {
    console.error('Get Simulations List API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch simulation history');
  }
};

// Get Single Simulation Details (Admin)
export const getSimulationDetails = async (simulationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/admin/simulations/${simulationId}`, config);
    return response.data; // Should be the full simulation document
  } catch (error) {
    console.error('Get Simulation Details API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch simulation details');
  }
};

// Request Review for a Simulation (Admin)
export const requestSimulationReview = async (simulationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/admin/simulations/${simulationId}/request-review`, {}, config);
    return response.data; // Should be the updated simulation document with the review
  } catch (error) {
    console.error('Request Simulation Review API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to request simulation review');
  }
};

// Generate Summary for a Simulation (Admin)
export const generateSimulationSummary = async (simulationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/admin/simulations/${simulationId}/summarize`, {}, config);
    return response.data; // Should be the updated simulation document with the summary
  } catch (error) {
    console.error('Generate Simulation Summary API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to generate simulation summary');
  }
};

// Get Simulation Export Configuration
export const getSimulationExportConfig = async (simulationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/simulation/${simulationId}/export/config`, config);
    return response.data.data; // Return the config data
  } catch (error) {
    console.error('Get Simulation Export Config API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to get export configuration');
  }
};

// Export Simulation
export const exportSimulation = async (simulationId, exportConfig, token) => {
  try {
    const config = {
      headers: { 'x-auth-token': token },
      responseType: exportConfig.format === 'json' ? 'json' : 'arraybuffer'
    };
    
    const response = await apiClient.post(
      `/simulation/${simulationId}/export`,
      exportConfig,
      config
    );
    
    return response; // Return full response for blob handling
  } catch (error) {
    console.error('Export Simulation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to export simulation');
  }
};

// --- Admin Review Prompt API Calls ---

// Get Review Prompt (Admin)
export const getReviewPrompt = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/review-prompt', config);
    return response.data; // Should be { prompt: '...' }
  } catch (error) {
    console.error('Get Review Prompt API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch review prompt');
  }
};

// Update Review Prompt (Admin)
export const updateReviewPrompt = async (data, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/admin/review-prompt', data, config);
    return response.data; // Should be { msg: '...' }
  } catch (error) {
    console.error('Update Review Prompt API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update review prompt');
  }
};


export const addUser = async (userData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/users', userData, config);
    return response.data;
  } catch (error) {
    console.error('Add User API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to add user');
  }
};

export const deleteUser = async (userId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/admin/users/${userId}`, config);
    return response.data;
  } catch (error) {
    console.error('Delete User API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete user');
  }
};

export const setUserStatus = async (userId, status, sendActivationEmail, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/status`, { status, sendActivationEmail }, config);
    return response.data;
  } catch (error) {
    console.error('Set User Status API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update user status');
  }
};

export const updateUserDetails = async (userId, details, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/details`, details, config);
    return response.data;
  } catch (error) {
    console.error('Update User Details API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update user details');
  }
};

export const resetUserPassword = async (userId, passwordData, token) => {
  try {
    // passwordData can be either a string (for backward compatibility) or an object with newPassword and forcePasswordChange
    const newPassword = typeof passwordData === 'string' ? passwordData : passwordData.newPassword;
    const forcePasswordChange = typeof passwordData === 'object' ? passwordData.forcePasswordChange : undefined;
    
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/admin/users/${userId}/reset-password`, { 
      newPassword, 
      forcePasswordChange 
    }, config);
    return response.data;
  } catch (error) {
    console.error('Reset User Password API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to reset user password');
  }
};

export const assignTherapistForClient = async (userId, therapistId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/assigned-therapist`, { therapistId }, config);
    return response.data;
  } catch (error) {
    console.error('Assign Therapist For Client API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to assign therapist');
  }
};

// --- Admin User Management API Calls ---

// Get All Users (Admin)
export const getAllUsers = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/users', config);
    return response.data; // Should be an array of user objects
  } catch (error) {
    console.error('Get All Users API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch users');
  }
};

// Update Therapist ElevenLabs Voice ID (Admin)
export const updateTherapistElevenLabsVoiceId = async (userId, elevenLabsVoiceId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/elevenlabs-voiceid`, { elevenLabsVoiceId }, config);
    return response.data; // Should be { msg: '...', user: {...} }
  } catch (error) {
    console.error('Update Therapist ElevenLabs Voice ID API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update therapist ElevenLabs Voice ID');
  }
};

// Get available ElevenLabs voices — via the server's cached, authenticated
// endpoint (GET /api/tts/voices). The previous direct api.elevenlabs.io call
// required shipping the API key in the web bundle (key-removal plan, Phase 1.1).
// The server returns a standardized voice shape; map it back to the legacy
// ElevenLabs shape (voice_id / labels / preview_url) so existing consumers
// (admin config screens, training voice picker) keep working unchanged.
export const getElevenLabsVoices = async () => {
  try {
    const response = await apiClient.get('/tts/voices', { params: { provider: 'elevenlabs' } });
    const voices = response.data?.voices || [];
    return voices.map(v => ({
      voice_id: v.providerVoiceId || v.id,
      name: v.name,
      labels: v.metadata?.labels || {
        gender: v.gender,
        accent: v.accent,
        age: v.ageGroup,
        use_case: v.useCase,
        description: v.description,
      },
      preview_url: v.previewUrl,
      category: v.category,
    }));
  } catch (error) {
    console.error('Failed to fetch ElevenLabs voices:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch voices');
  }
};

// Set User Therapist Role (Admin)
export const setUserTherapistRole = async (userId, isTherapist, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/therapist-role`, { isTherapist }, config);
    return response.data; // Should be { msg: '...', user: {...} }
  } catch (error) {
    console.error('Set User Therapist Role API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update user role');
  }
};

// Update User Roles (Admin) - Flexible multi-role management
export const updateUserRoles = async (userId, rolesData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // rolesData can be either an array (for backward compatibility) or an object { roles, therapistInfo, supervisorInfo }
    const payload = Array.isArray(rolesData) ? { roles: rolesData } : rolesData;
    const response = await apiClient.put(`/admin/users/${userId}/roles`, payload, config);
    return response.data; // Should be { msg: '...', user: {...} }
  } catch (error) {
    console.error('Update User Roles API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update user roles');
  }
};


// --- Therapist API Calls ---

// Add Annotation to Message (Therapist)
export const addAnnotation = async (conversationId, messageId, annotationData, token) => {
  // annotationData should be { text: string, tags?: string[] }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    
    // Format data according to server expectations:
    // Server expects: { messageId: string, annotation: string, tags?: string[] }
    const requestData = {
      messageId: messageId,
      annotation: annotationData.text,
      tags: annotationData.tags || []
    };
    
    console.log('Sending annotation data:', JSON.stringify(requestData, null, 2));
    
    const response = await apiClient.post(`/therapist/conversation/${conversationId}/message/${messageId}/annotate`, requestData, config);
    return response.data; // Should be { msg: '...', annotation: {...} }
  } catch (error) {
    console.error('Add Annotation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to add annotation');
  }
};

// Edit Annotation (Therapist)
export const editAnnotation = async (conversationId, messageId, annotationId, annotationData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/therapist/conversation/${conversationId}/message/${messageId}/annotation/${annotationId}`, annotationData, config);
    return response.data; // Should be { msg: '...', annotation: {...} }
  } catch (error) {
    console.error('Edit Annotation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to edit annotation');
  }
};

// Delete Annotation (Therapist)
export const deleteAnnotation = async (conversationId, messageId, annotationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/therapist/conversation/${conversationId}/message/${messageId}/annotation/${annotationId}`, config);
    return response.data; // Should be { msg: '...' }
  } catch (error) {
    console.error('Delete Annotation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete annotation');
  }
};

// Handback Conversation to AI (Therapist)
export const handbackToAI = async (conversationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // POST request to the specific conversation's handback endpoint
    const response = await apiClient.post(`/therapist/conversation/${conversationId}/handback-to-ai`, {}, config);
    return response.data; // Should be { msg: '...', status: '...', addedMessage: {...} }
  } catch (error) {
    console.error('Handback to AI API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to handback conversation to AI');
  }
};

// Soft Delete Conversation (Therapist) - Hide from client view
export const softDeleteConversation = async (conversationId, reason, token) => {
  try {
    const config = token ? { 
      headers: { 'x-auth-token': token },
      data: { reason } // DELETE requests with body need data field
    } : {};
    const response = await apiClient.delete(`/therapist/conversation/${conversationId}/soft-delete`, config);
    return response.data; // Should be { msg: '...', conversationId: '...', clientName: '...', messageCount: ... }
  } catch (error) {
    console.error('Soft Delete Conversation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete conversation');
  }
};

// Restore a soft-deleted conversation
export const undeleteConversation = async (conversationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/therapist/conversation/${conversationId}/undelete`, {}, config);
    return response.data; // Should be { msg: '...', conversationId: '...', clientName: '...', messageCount: ... }
  } catch (error) {
    console.error('Undelete Conversation API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to restore conversation');
  }
};

// Get Assigned Clients (Therapist)
export const getAssignedClients = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/therapist/clients', config);
    return response.data; // Should be an array of client user objects
  } catch (error) {
    console.error('Get Assigned Clients API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch assigned clients');
  }
};

// --- Synthetic Clients / Training Clients (Therapist) ---
// These call backend routes provided by the synthetic-clients-in-therapist-ui
// feature (see docs/synthetic-clients-in-therapist-ui/). They are kept as a
// distinct API surface from real-client helpers so accidental misuse falls
// back to the default-safe real-client paths.

/**
 * List the calling therapist's active synthetic clients (users with
 * `kind: 'synthetic'`). Backend route: GET /api/therapist/training-clients
 *
 * Each row in the response is a User-shaped object with the additional
 * `kind: 'synthetic'`, `syntheticProfile`, `trainingSession` and last-activity
 * metadata the list view needs.
 */
export const getMyTrainingClients = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/therapist/training-clients', config);
    // Backend may return either `[ ... ]` or `{ clients: [ ... ] }`.
    return response.data;
  } catch (error) {
    console.error('Get Training Clients API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to fetch training clients');
  }
};

/**
 * Start a therapist-practice training session and materialise a synthetic
 * client `User` document so the resulting conversation is openable inside
 * the full ClientDetailView surface.
 *
 * Calls the existing training-session start route — `mode` is fixed to
 * `'therapist_practice'` and `materialiseAsClient` defaults to `true` so the
 * backend creates the synthetic User + linked Conversation per the spec
 * (extends the existing route rather than introducing a new one).
 *
 * @returns {Promise<object>} Response payload (typically including
 *   `syntheticUserId`, `conversationId`, and a navigable `route`).
 */
export const startTherapistPracticeSession = async (
  { scenarioId, materialiseAsClient = true } = {},
  token
) => {
  try {
    if (!scenarioId) {
      throw new Error('scenarioId is required to start a practice session');
    }
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const body = {
      mode: 'therapist_practice',
      scenarioId,
      materialiseAsClient,
    };
    const response = await apiClient.post('/training/sessions/start', body, config);
    return response.data;
  } catch (error) {
    console.error('Start Therapist Practice Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to start practice session');
  }
};

// Pause an in-progress training session (existing training session lifecycle route).
export const pauseTrainingSession = async (sessionId, token) => {
  try {
    if (!sessionId) throw new Error('sessionId is required');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/training/sessions/${sessionId}/pause`, {}, config);
    return response.data;
  } catch (error) {
    console.error('Pause Training Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to pause practice session');
  }
};

// Resume a paused training session (existing training session lifecycle route).
export const resumeTrainingSession = async (sessionId, token) => {
  try {
    if (!sessionId) throw new Error('sessionId is required');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/training/sessions/${sessionId}/resume`, {}, config);
    return response.data;
  } catch (error) {
    console.error('Resume Training Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to resume practice session');
  }
};

// End / complete a training session (existing training session lifecycle route).
export const endTrainingSession = async (sessionId, token, reason = 'student_concluded') => {
  try {
    if (!sessionId) throw new Error('sessionId is required');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(
      `/training/sessions/${sessionId}/complete`,
      { reason },
      config
    );
    return response.data;
  } catch (error) {
    console.error('End Training Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to end practice session');
  }
};

/**
 * Start (or resume) the AI-mode auto-run loop on a synthetic conversation.
 * Fire-and-forget on the server — messages stream in via the existing
 * 'new_message' socket event as each turn lands. The loop pauses naturally
 * when `pauseTrainingSession` flips `TrainingSession.pausedAt`.
 */
/**
 * Hard-delete a synthetic (training) client and every artefact it owns
 * (Conversations, Goals, Journal, CareNotes, Formulations, ActivityEvents,
 * Alerts, ReasoningTraces, the originating TrainingSession). Synthetic data
 * only — never touches real clients. Idempotent: already-gone clients return 204.
 */
export const deleteTrainingClient = async (syntheticUserId, token) => {
  try {
    if (!syntheticUserId) throw new Error('syntheticUserId is required');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(
      `/therapist/training-clients/${syntheticUserId}`,
      config
    );
    return response.data ?? { ok: true };
  } catch (error) {
    console.error('Delete Training Client API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to delete training client');
  }
};

export const startSyntheticAutoRun = async (conversationId, token) => {
  try {
    if (!conversationId) throw new Error('conversationId is required');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(
      `/therapist/synthetic-conversations/${conversationId}/auto-run`,
      {},
      config
    );
    return response.data;
  } catch (error) {
    console.error('Start Synthetic Auto-Run API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to start the practice conversation');
  }
};

/**
 * List the platform-authored scenarios available to the calling therapist
 * (used by the "Start new practice" picker). Wraps the existing
 * `simulation-governance/therapist/available` endpoint used by the
 * Simulation Lab page.
 */
export const getAvailableTherapistScenarios = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/simulation-governance/therapist/available', config);
    return response.data;
  } catch (error) {
    console.error('Get Available Therapist Scenarios API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || error.response?.data?.msg || 'Failed to fetch available scenarios');
  }
};

// Get Client Conversations (Therapist)
export const getClientConversations = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/conversations`, config);
    return response.data; // Should be an array of conversation objects
  } catch (error) {
    console.error('Get Client Conversations API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client conversations');
  }
};

// Request Conversation Review (Therapist)
export const requestConversationReview = async (conversationId, token, reviewerPersonaId = null) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const body = reviewerPersonaId ? { reviewerPersonaId } : {};
    const response = await apiClient.post(`/therapist/review/${conversationId}`, body, config);
    return response.data; // Should be { msg: '...', review: {...} }
  } catch (error) {
    console.error('Request Conversation Review API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to request conversation review');
  }
};

// Get Therapist Reviewer Personas
export const getTherapistReviewerPersonas = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/therapist/reviewer-personas', config);
    return response.data;
  } catch (error) {
    console.error('Get Reviewer Personas API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch reviewer personas');
  }
};

// Get Client Check-Ins (Therapist)
export const getClientCheckIns = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/checkins`, config);
    return response.data; // Should be an array of check-in objects
  } catch (error) {
    console.error('Get Client Check-Ins API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client check-ins');
  }
};

// Get Client Goals (Therapist)
export const getTherapistClientGoals = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/goals`, config);
    return response.data; // Should be an array of goal objects
  } catch (error) {
    console.error('Get Client Goals (Therapist) API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client goals');
  }
};

// Get Client Journal Entries (Therapist)
export const getTherapistClientJournal = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/journal`, config);
    return response.data; // Should be an array of journal entry objects
  } catch (error) {
    console.error('Get Client Journal (Therapist) API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client journal entries');
  }
};

// Get Client Progress Data (Therapist)
export const getTherapistClientProgress = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/progress`, config);
    return response.data; // Should be the progress dashboard data object
  } catch (error) {
    console.error('Get Client Progress (Therapist) API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client progress data');
  }
};

// Get Client Onboarding Details (Therapist)
export const getClientOnboardingDetails = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/onboarding`, config);
    return response.data;
  } catch (error) {
    console.error('Get Client Onboarding Details API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client onboarding details');
  }
};

// Get Client Triage Details (Therapist)
export const getClientTriageDetails = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/triage`, config);
    return response.data;
  } catch (error) {
    console.error('Get Client Triage Details API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client triage details');
  }
};

// Get Therapist UI Preferences (current user)
export const getTherapistPreferences = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/therapist/preferences', config);
    return response.data;
  } catch (error) {
    console.error('Get Therapist Preferences API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch therapist preferences');
  }
};

// Update Therapist UI Preferences (current user) - accepts a partial preferences object
export const updateTherapistPreferences = async (prefs, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.patch('/therapist/preferences', prefs, config);
    return response.data;
  } catch (error) {
    console.error('Update Therapist Preferences API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update therapist preferences');
  }
};

// Get Client Care Notes (Therapist)
export const getClientCareNotes = async (clientId, includeInactive = false, token) => {
  try {
    if (!clientId) {
      console.error('getClientCareNotes called with undefined clientId');
      return { careNotes: [] };
    }
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/client/${clientId}/care-notes${includeInactive ? '?includeInactive=true' : ''}`, config);
    return response.data; // Should be an array of care note objects
  } catch (error) {
    console.error('Get Client Care Notes API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch client care notes');
  }
};


// --- Platform Settings API Calls (Admin) ---

// Get platform settings
export const getPlatformSettings = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/platform-settings/settings', config);
    return response.data; // Returns the platform settings object
  } catch (error) {
    console.error('Get Platform Settings API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch platform settings');
  }
};

// Update platform settings
export const updatePlatformSettings = async (settingsData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/platform-settings/settings', settingsData, config);
    return response.data; // Returns the updated settings object
  } catch (error) {
    console.error('Update Platform Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update platform settings');
  }
};

// Get list of treatment pathways (for therapist view)
export const getPathwaysList = async (token) => {
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.get('/platform-settings/pathways/list', config);
        return response.data; // Returns array of pathway objects
    } catch (error) {
        console.error('Get Pathways List API error:', error.response?.data || error.message);
        throw new Error(error.response?.data?.message || 'Failed to fetch treatment pathways list');
    }
};

// Add a treatment pathway
export const addTreatmentPathway = async (pathwayData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/platform-settings/treatment-pathways', pathwayData, config);
    return response.data; // Returns the updated list of treatment pathways
  } catch (error) {
    console.error('Add Treatment Pathway API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to add treatment pathway');
  }
};

// Remove a treatment pathway
export const removeTreatmentPathway = async (pathwayName, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/platform-settings/treatment-pathways/${pathwayName}`, config);
    return response.data; // Returns the updated list of treatment pathways
  } catch (error) {
    console.error('Remove Treatment Pathway API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to remove treatment pathway');
  }
};

// --- LLM Configuration API Calls (Admin) ---

// Get LLM configuration settings
export const getLLMConfiguration = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/platform-settings/llm-configuration', config);
    return response.data;
  } catch (error) {
    console.error('Get LLM Configuration API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch LLM configuration');
  }
};

// --- Unified LLM Config API (Admin) — component/serviceGroup/tool scoped ---

// List every unified LLM config, plus the valid component and provider lists
export const listLlmConfigs = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/llm-configs', config);
    return response.data;
  } catch (error) {
    console.error('List LLM configs API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch LLM configs');
  }
};

// Ask which model is actually in force for a component, and which layer decided it
export const resolveLlmConfig = async ({ component, serviceGroupId, tool }, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const params = new URLSearchParams({ component });
    if (serviceGroupId) params.append('serviceGroupId', serviceGroupId);
    if (tool) params.append('tool', tool);
    const response = await apiClient.get(`/admin/llm-configs/resolve?${params.toString()}`, config);
    return response.data;
  } catch (error) {
    console.error('Resolve LLM config API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to resolve LLM config');
  }
};

// Create or update a config (upsert on component + serviceGroupId + tool)
export const saveLlmConfig = async (payload, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/llm-configs', payload, config);
    return response.data;
  } catch (error) {
    console.error('Save LLM config API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to save LLM config');
  }
};

export const deleteLlmConfig = async (id, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/admin/llm-configs/${id}`, config);
    return response.data;
  } catch (error) {
    console.error('Delete LLM config API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to delete LLM config');
  }
};

// Update LLM configuration settings
export const updateLLMConfiguration = async (llmConfiguration, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/platform-settings/llm-configuration', { llmConfiguration }, config);
    return response.data;
  } catch (error) {
    console.error('Update LLM Configuration API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update LLM configuration');
  }
};

// Get available LLM models (can filter by provider)
export const getAvailableLLMModels = async (token, options = {}) => {
  try {
    const { provider = null, activeOnly = true } = options;
    const config = {
      headers: token ? { 'x-auth-token': token } : {},
      params: {}
    };
    if (provider) config.params.provider = provider;
    if (activeOnly !== null) config.params.activeOnly = activeOnly;
    const response = await apiClient.get('/platform-settings/available-models', config);
    return response.data;
  } catch (error) {
    console.error('Get Available LLM Models API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch available models');
  }
};

// Add a new model to available models
export const addAvailableModel = async (modelData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/platform-settings/available-models', modelData, config);
    return response.data;
  } catch (error) {
    console.error('Add Available Model API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to add model');
  }
};

// Update an existing model
export const updateAvailableModel = async (provider, modelId, updateData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/platform-settings/available-models/${provider}/${modelId}`, updateData, config);
    return response.data;
  } catch (error) {
    console.error('Update Available Model API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update model');
  }
};

// Remove/deprecate a model
export const removeAvailableModel = async (provider, modelId, hardDelete = false, token) => {
  try {
    const config = {
      headers: token ? { 'x-auth-token': token } : {},
      params: { hardDelete: hardDelete ? 'true' : 'false' }
    };
    const response = await apiClient.delete(`/platform-settings/available-models/${provider}/${modelId}`, config);
    return response.data;
  } catch (error) {
    console.error('Remove Available Model API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to remove model');
  }
};

// --- Client Therapy Settings API Calls (Therapist) ---

// Get client therapy settings
export const getClientTherapySettings = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/platform-settings/client/${clientId}/settings`, config);
    return response.data; // Returns the client's therapy settings object
  } catch (error) {
    console.error('Get Client Therapy Settings API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch client therapy settings');
  }
};

// Update client therapy settings
export const updateClientTherapySettings = async (clientId, settingsData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/platform-settings/client/${clientId}/settings`, { therapySettings: settingsData }, config);
    return response.data; // Returns the updated client therapy settings object
  } catch (error) {
    console.error('Update Client Therapy Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update client therapy settings');
  }
};

// --- Session API Calls ---

// Start a new therapy session
export const startSession = async (conversationId, goalIds, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/sessions/conversations/${conversationId}/sessions/start`, { goalIds }, config);
    return response.data; // Returns { message: '...', session: {...} }
  } catch (error) {
    console.error('Start Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to start session');
  }
};

// End the current therapy session
export const endSession = async (conversationId, generateSummary, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/sessions/conversations/${conversationId}/sessions/end`, { generateSummary }, config);
    return response.data; // Returns { message: '...', session: {...}, nextSession: Date }
  } catch (error) {
    console.error('End Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to end session');
  }
};

// Get session history for a conversation
export const getSessionHistory = async (conversationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/sessions/conversations/${conversationId}/sessions`, config);
    return response.data; // Returns array of session objects
  } catch (error) {
    console.error('Get Session History API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch session history');
  }
};

// Get a specific session
export const getSession = async (conversationId, sessionId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/sessions/conversations/${conversationId}/sessions/${sessionId}`, config);
    return response.data; // Returns { session: {...}, messages: [...] }
  } catch (error) {
    console.error('Get Session API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch session details');
  }
};

// Update a session (therapist only)
export const updateSession = async (conversationId, sessionId, updateData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/sessions/conversations/${conversationId}/sessions/${sessionId}`, updateData, config);
    return response.data; // Returns { message: '...', session: {...} }
  } catch (error) {
    console.error('Update Session API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update session');
  }
};

// --- Goal API Calls ---

// Create a new goal
export const createGoal = async (goalData, token) => {
  // goalData should be { description: string, goalType?: 'one_off' | 'habit' }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // Send the whole goalData object
    const response = await apiClient.post('/goals', goalData, config);
    return response.data; // Returns the newly created goal object
  } catch (error) {
    console.error('Create Goal API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Failed to create goal');
  }
};

// Get all goals for the user
export const getGoals = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/goals', config);
    return response.data; // Returns an array of goal objects
  } catch (error) {
    console.error('Get Goals API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch goals');
  }
};

// Update a specific goal
export const updateGoal = async (goalId, updateData, token) => {
  // updateData can include { description?: string, status?: string, goalType?: string }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/goals/${goalId}`, updateData, config);
    return response.data; // Returns the updated goal object
  } catch (error) {
    console.error('Update Goal API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.errors?.map(e => e.msg).join(', ') || 'Failed to update goal');
  }
};

// Delete a specific goal
export const deleteGoal = async (goalId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/goals/${goalId}`, config);
    return response.data; // Returns { msg: 'Goal removed successfully' }
  } catch (error) {
    console.error('Delete Goal API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to delete goal');
  }
};

// --- Check-In API Calls ---

// Create a new check-in
export const createCheckIn = async (checkInData, token) => {
  // checkInData should be { moodRating: number, anxietyRating: number, notes?: string }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/checkins', checkInData, config);
    return response.data; // Returns the newly created check-in object
  } catch (error) {
    console.error('Create Check-In API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to create check-in');
  }
};

// Get all check-ins for the user
export const getCheckIns = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/checkins', config);
    return response.data; // Returns an array of check-in objects
  } catch (error) {
    console.error('Get Check-Ins API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch check-ins');
  }
};

// --- Check-In Template API Calls ---

// Get my check-in template (client endpoint)
export const getMyCheckInTemplate = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/checkin-templates/current/for-me', config);
    return response.data; // Returns { success: true, template: {...} }
  } catch (error) {
    console.error('Get My Check-In Template API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch check-in template');
  }
};

// Get check-in template for a specific client (therapist/admin)
export const getClientCheckInTemplate = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/checkin-templates/for-client/${clientId}`, config);
    return response.data; // Returns { success: true, template: {...} }
  } catch (error) {
    console.error('Get Client Check-In Template API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch client check-in template');
  }
};

// Get all check-in templates (admin)
export const getAllCheckInTemplates = async (token, category = null) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const url = category ? `/checkin-templates?category=${category}` : '/checkin-templates';
    const response = await apiClient.get(url, config);
    return response.data; // Returns { success: true, templates: [...] }
  } catch (error) {
    console.error('Get All Check-In Templates API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch check-in templates');
  }
};

// Submit a template-based check-in
export const submitTemplateCheckIn = async (templateId, responses, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/checkin-templates/${templateId}/submit`, { responses }, config);
    return response.data; // Returns { success: true, checkIn: {...} }
  } catch (error) {
    console.error('Submit Template Check-In API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to submit check-in');
  }
};

// Get template-based check-in history
export const getTemplateCheckInHistory = async (token, options = {}) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const params = [];
    if (options.templateId) params.push(`templateId=${options.templateId}`);
    if (options.startDate) params.push(`startDate=${options.startDate}`);
    if (options.endDate) params.push(`endDate=${options.endDate}`);
    if (options.limit) params.push(`limit=${options.limit}`);
    let url = '/checkin-templates/history';
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await apiClient.get(url, config);
    return response.data; // Returns { success: true, history: [...] }
  } catch (error) {
    console.error('Get Template Check-In History API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch check-in history');
  }
};

// Get check-in chart data
export const getCheckInChartData = async (clientId, templateId, days, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    let url = clientId ? `/checkin-templates/charts/${clientId}` : '/checkin-templates/charts';
    const params = [];
    if (templateId) params.push(`templateId=${templateId}`);
    if (days) params.push(`days=${days}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await apiClient.get(url, config);
    return response.data; // Returns { success: true, template, series, dateRange, totalCheckIns }
  } catch (error) {
    console.error('Get Check-In Chart Data API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch chart data');
  }
};

// --- Treatment Pathway Transition API Calls ---

// Get client's pathway information
export const getClientPathway = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/pathways/client/${clientId}`, config);
    return response.data; // Returns pathway info with readiness
  } catch (error) {
    console.error('Get Client Pathway API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch client pathway');
  }
};

// Evaluate phase readiness
export const evaluatePhaseReadiness = async (clientId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/pathways/client/${clientId}/readiness`, config);
    return response.data; // Returns { success: true, evaluation: {...} }
  } catch (error) {
    console.error('Evaluate Phase Readiness API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to evaluate readiness');
  }
};

// Request phase transition
export const requestPhaseTransition = async (clientId, targetPhase, notes, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/pathways/client/${clientId}/request-transition`, { targetPhase, notes }, config);
    return response.data;
  } catch (error) {
    console.error('Request Phase Transition API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to request transition');
  }
};

// Approve phase transition (therapist action)
export const approvePhaseTransition = async (clientId, notes, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/pathways/client/${clientId}/approve-transition`, { notes }, config);
    return response.data;
  } catch (error) {
    console.error('Approve Phase Transition API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to approve transition');
  }
};

// Reject phase transition (therapist action)
export const rejectPhaseTransition = async (clientId, reason, notes, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/pathways/client/${clientId}/reject-transition`, { reason, notes }, config);
    return response.data;
  } catch (error) {
    console.error('Reject Phase Transition API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to reject transition');
  }
};

// Attest readiness criterion (therapist action)
export const attestReadinessCriterion = async (clientId, criterionId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(`/pathways/client/${clientId}/attest-criterion`, { criterionId }, config);
    return response.data;
  } catch (error) {
    console.error('Attest Readiness Criterion API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to attest criterion');
  }
};

// Get clients with pending phase transitions (therapist view)
export const getClientsWithPendingTransitions = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/pathways/pending-transitions', config);
    return response.data; // Returns { success: true, clients: [...] }
  } catch (error) {
    console.error('Get Pending Transitions API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Failed to fetch pending transitions');
  }
};

// --- Session Summary API Calls ---

// Generate summaries and prompts (Therapist action)
export const generateSessionSummary = async (conversationId, token) => {
  try {
    console.log('API: Attempting to generate session summary with token:', token ? 'Token Present' : 'No Token');
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // POST request to trigger generation
    const response = await apiClient.post(`/conversations/${conversationId}/generate-summary`, {}, config);
    console.log('API: Session summary generation successful');
    return response.data; // Should be { message: '...', clientSummary: '...', therapistSummary: '...', reflectionPrompts: [...] }
  } catch (error) {
    console.error('Generate Session Summary API error:', error.response ? error.response.status : 'No response', error.response?.data || error.message);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to generate summaries. Only therapists can access this feature.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to generate session summary');
    }
  }
};

// Get client summary and prompts (Client action)
export const getClientSessionSummary = async (conversationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/conversations/${conversationId}/summary`, config);
    return response.data; // Should be { clientSummary: '...', reflectionPrompts: [...] }
  } catch (error) {
    console.error('Get Client Session Summary API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch client session summary');
  }
};

// Get therapist summary (Therapist action)
export const getTherapistSessionSummary = async (conversationId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/therapist/conversations/${conversationId}/summary`, config);
    return response.data; // Should be { therapistSummary: '...' }
  } catch (error) {
    console.error('Get Therapist Session Summary API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch therapist session summary');
  }
};




// --- Journal API Calls ---

// Create a new journal entry
export const createJournalEntry = async (entryData, token) => {
  // entryData can include { content, title, entryType, relatedGoal, behaviourTracked, completionStatus, effortRating }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/journal', entryData, config);
    return response.data; // Returns the newly created entry object
  } catch (error) {
    console.error('Create Journal Entry API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to create journal entry');
  }
};

// Get all journal entries for the user
export const getJournalEntries = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/journal', config);
    return response.data; // Returns an array of entry objects
  } catch (error) {
    console.error('Get Journal Entries API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch journal entries');
  }
};

// Get a single journal entry by ID
export const getJournalEntryById = async (entryId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(`/journal/${entryId}`, config);
    return response.data; // Returns the specific entry object
  } catch (error) {
    console.error('Get Single Journal Entry API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch journal entry');
  }
};

// Update a journal entry
export const updateJournalEntry = async (entryId, updateData, token) => {
  // updateData can include { content, title, entryType, relatedGoal, behaviourTracked, completionStatus, effortRating }
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/journal/${entryId}`, updateData, config);
    return response.data; // Returns the updated entry object
  } catch (error) {
    console.error('Update Journal Entry API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update journal entry');
  }
};

// Delete a journal entry
export const deleteJournalEntry = async (entryId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/journal/${entryId}`, config);
    return response.data; // Returns { message: '...' }
  } catch (error) {
    console.error('Delete Journal Entry API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to delete journal entry');
  }
};

// --- Progress Dashboard API Call ---
export const getProgressData = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/progress/dashboard', config);
    return response.data; // Returns { checkIns, goals, habitTracking, stats }
  } catch (error) {
    console.error('Get Progress Data API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch progress data');
  }
};


// Export the utility function as well if needed elsewhere
export { setAuthToken };

// --- Therapist Conversation Control API Calls ---
export const setConversationMode = async (conversationId, mode, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/therapist/conversation/${conversationId}/mode`, { mode }, config);
    return response.data; // Should be { msg: '...', mode: '...', status: '...', systemMessage?: {...} }
  } catch (error) {
    console.error('Set Conversation Mode API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to set conversation mode');
  }
};

export const toggleAutoForward = async (conversationId, newState, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // Send the new state in the request body
    const response = await apiClient.put(`/therapist/conversation/${conversationId}/auto-forward`, { enabled: newState }, config);
    return response.data; // Should be { msg: '...', autoForwardEnabled: boolean }
  } catch (error) {
    console.error('Toggle Auto-Forward API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to toggle auto-forward');
  }
};

// Handle AI-suggested mode change (approve/reject)
export const handleModeChangeSuggestion = async (conversationId, actionData, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(
      `/therapist/conversation/${conversationId}/handle-mode-suggestion`, 
      actionData, // { action: 'approve' or 'reject', suggestedMode, reason, detectedContext, urgency }
      config
    );
    return response.data; // Returns response based on action (approval or rejection)
  } catch (error) {
    console.error('Handle Mode Change Suggestion API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to process mode change suggestion');
  }
};

// --- Admin User Specialization Management ---
// Get list of available modalities/specializations
export const getAvailableModalities = async (token) => {
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.get('/admin/modalities', config);
        return response.data; // Should be an array of strings
    } catch (error) {
        console.error('Get Available Modalities API error:', error.response?.data || error.message);
        throw new Error(error.response?.data?.msg || 'Failed to fetch available modalities');
    }
};

// Update specializations for a specific therapist user
export const updateTherapistSpecializations = async (userId, specializations, token) => {
    try {
        const config = token ? { headers: { 'x-auth-token': token } } : {};
        const response = await apiClient.put(`/admin/users/${userId}/specializations`, { specializations }, config);
        return response.data; // Should be { msg: '...', user: {...} }
    } catch (error) {
        console.error('Update Therapist Specializations API error:', error.response?.data || error.message);
        throw new Error(error.response?.data?.msg || 'Failed to update therapist specializations');
    }
};

// --- Therapist Assignment API Calls ---

// Update Client Assignment Algorithm (Admin)
export const updateClientAssignmentAlgorithm = async (algorithm, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/admin/client-assignment-algorithm', 
      { algorithm }, config);
    return response.data;
  } catch (error) {
    console.error('Update Client Assignment Algorithm API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update client assignment algorithm');
  }
};

// Update Therapist Auto-Assignment Setting (Admin)
export const updateTherapistAutoAssignment = async (userId, canReceiveAutoAssignments, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(`/admin/users/${userId}/auto-assignment`, 
      { canReceiveAutoAssignments }, config);
    return response.data;
  } catch (error) {
    console.error('Update Therapist Auto-Assignment API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to update therapist auto-assignment setting');
  }
};

// Get Therapist Assignment Stats (Admin)
export const getAssignmentStats = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/admin/assignment-stats', config);
    return response.data;
  } catch (error) {
    console.error('Get Assignment Stats API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch assignment statistics');
  }
};

// Get Therapist's Own Assignment Stats (for therapist view)
export const getTherapistAssignmentStats = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/therapist/assignment-stats', config);
    return response.data;
  } catch (error) {
    console.error('Get Therapist Assignment Stats API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch therapist assignment statistics');
  }
};

// --- Staff Management API Calls (Admin) ---

// Get paginated users with filtering and search
export const getPaginatedUsers = async (params, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token }, params } : { params };
    const response = await apiClient.get('/admin/users/paginated', config);
    return response.data; // { users, pagination }
  } catch (error) {
    console.error('Get Paginated Users API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to fetch paginated users');
  }
};

// Search users with autocomplete
export const searchUsers = async (params, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token }, params } : { params };
    const response = await apiClient.get('/admin/users/search', config);
    return response.data; // { users, hasMore }
  } catch (error) {
    console.error('Search Users API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to search users');
  }
};

// Bulk assign clients to a therapist
export const bulkAssignClientsToTherapist = async (clientIds, therapistId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/clients/bulk-assign', { clientIds, therapistId }, config);
    return response.data;
  } catch (error) {
    console.error('Bulk Assign Clients API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to bulk assign clients');
  }
};

// Bulk assign therapists to supervisors
export const bulkAssignTherapistsToSupervisors = async (therapistIds, supervisorIds, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/therapists/bulk-assign-supervisors',
      { therapistIds, supervisorIds }, config);
    return response.data;
  } catch (error) {
    console.error('Bulk Assign Supervisors API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to bulk assign supervisors');
  }
};

// Remove therapist from supervisor
export const removeTherapistFromSupervisor = async (supervisorId, therapistId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(`/admin/supervisors/${supervisorId}/therapists/${therapistId}`, config);
    return response.data;
  } catch (error) {
    console.error('Remove Therapist From Supervisor API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to remove therapist from supervisor');
  }
};

// Transfer clients from one therapist to another
export const transferClients = async (clientIds, fromTherapistId, toTherapistId, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post('/admin/clients/transfer',
      { clientIds, fromTherapistId, toTherapistId }, config);
    return response.data;
  } catch (error) {
    console.error('Transfer Clients API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to transfer clients');
  }
};

// Bulk update user status
export const bulkUpdateUserStatus = async (userIds, newStatus, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/admin/users/bulk-status', { userIds, newStatus }, config);
    return response.data;
  } catch (error) {
    console.error('Bulk Update User Status API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to bulk update user status');
  }
};

// Export users to CSV
export const exportUsers = async (params, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token }, params, responseType: 'text' } : { params, responseType: 'text' };
    const response = await apiClient.get('/admin/users/export', config);
    return response.data; // Returns CSV text
  } catch (error) {
    console.error('Export Users API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.msg || 'Failed to export users');
  }
};

// Get AI mode change settings
export const getAiModeChangeSettings = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/platform-settings/ai-mode-change-settings', config);
    return response.data; // API now returns just the AI mode change settings
  } catch (error) {
    console.error('Get AI Mode Change Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch AI mode change settings');
  }
};

// Update AI mode change settings
export const updateAiModeChangeSettings = async (settings, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/platform-settings/ai-mode-change-settings',
      { aiModeChangeSettings: settings },
      config
    );
    return response.data; // Returns { message: '...', settings: {...} }
  } catch (error) {
    console.error('Update AI Mode Change Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update AI mode change settings');
  }
};

// Get content safety screening settings
export const getContentSafetyScreeningSettings = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/platform-settings/content-safety-screening', config);
    return response.data;
  } catch (error) {
    console.error('Get Content Safety Screening Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch content safety screening settings');
  }
};

// Update content safety screening settings
export const updateContentSafetyScreeningSettings = async (settings, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put('/platform-settings/content-safety-screening',
      { contentSafetyScreeningSettings: settings },
      config
    );
    return response.data; // Returns { message: '...', settings: {...}, changesLogged: number }
  } catch (error) {
    console.error('Update Content Safety Screening Settings API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to update content safety screening settings');
  }
};

// Get default content safety keywords
export const getContentSafetyDefaultKeywords = async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/platform-settings/content-safety-screening/default-keywords', config);
    return response.data;
  } catch (error) {
    console.error('Get Content Safety Default Keywords API error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to fetch default safety keywords');
  }
};

// Generic HTTP request methods
const get = async (url, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get(url, config);
    return response;
  } catch (error) {
    console.error(`GET request error for ${url}:`, error.response?.data || error.message);
    throw error;
  }
};

const post = async (url, data, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.post(url, data, config);
    return response;
  } catch (error) {
    console.error(`POST request error for ${url}:`, error.response?.data || error.message);
    throw error;
  }
};

const put = async (url, data, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.put(url, data, config);
    return response;
  } catch (error) {
    console.error(`PUT request error for ${url}:`, error.response?.data || error.message);
    throw error;
  }
};

const patch = async (url, data, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.patch(url, data, config);
    return response;
  } catch (error) {
    console.error(`PATCH request error for ${url}:`, error.response?.data || error.message);
    throw error;
  }
};

const del = async (url, token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.delete(url, config);
    return response;
  } catch (error) {
    console.error(`DELETE request error for ${url}:`, error.response?.data || error.message);
    throw error;
  }
};

// Default export object containing all API functions
const apiClient_default = {
  // Generic HTTP methods
  get,
  post,
  put,
  patch,
  delete: del, // Use 'del' as function name to avoid JS keyword conflict

  // Therapist Conversation Control
  setConversationMode,
  toggleAutoForward,
  handleModeChangeSuggestion,

  register,
  forgotPassword,
  resetPassword,
  verifyMfa,
  login,
  getUser,
  sendMessage,
  getHistory,
  getTherapistPersonas,
  deleteAccount,
  generateSummary,
  getClientPersonas, // Keep the non-admin one for simulation runner
  runSimulation,
  getSimulationReview,
  // Admin functions
  getAdminClientPersonas,
  addClientPersona,
  updateClientPersona,
  deleteClientPersona,
  // Admin therapist functions
  getAdminTherapistPersonas,
  addTherapistPersona,
  updateTherapistPersona,
  deleteTherapistPersona,
  generateReviewerPersona,
  // Admin simulation history functions
  getSimulationsList,
  getSimulationDetails,
  requestSimulationReview, // Added
  generateSimulationSummary, // Added
  getSimulationExportConfig, // Added
  exportSimulation, // Added
  // Admin review prompt functions
  getReviewPrompt,
  updateReviewPrompt,
  // Admin User Management
  getAllUsers,
  setUserTherapistRole,
  getAvailableModalities, // Added
  updateTherapistSpecializations, // Added
  updateTherapistElevenLabsVoiceId, // Added
  getElevenLabsVoices, // Added

  // Activity Event Logging
  logActivityEvent,

  // Therapist Goal Proposal Handling
  handleTherapistGoalProposalAction: async (conversationId, payload, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/chat/therapist/handle-goal-proposal/${conversationId}`, payload, config);
      return response.data; // Expected: { msg: '...', ... }
    } catch (error) {
      console.error('Handle Therapist Goal Proposal Action API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to handle therapist action on goal proposal');
    }
  },

  // Therapist Functions
  getPathwayAssignmentData, // Added
  updateClientAssignedPathway, // Added
  getAssignedClients,
  // Synthetic clients / training-clients surface
  getMyTrainingClients,
  deleteTrainingClient,
  startTherapistPracticeSession,
  startSyntheticAutoRun,
  pauseTrainingSession,
  resumeTrainingSession,
  endTrainingSession,
  getAvailableTherapistScenarios,
  getClientConversations,
  requestConversationReview,
  softDeleteConversation, // Added for hiding spurious conversations
  undeleteConversation, // Added for restoring conversations
  getClientCheckIns,
  getTherapistClientGoals, // Added
  getTherapistClientJournal, // Added
  getTherapistClientProgress, // Added
  getClientOnboardingDetails, // Added onboarding details function
  getClientTriageDetails, // Added triage details function
  getTherapistPreferences, // Therapist UI preferences (current user)
  updateTherapistPreferences, // Update therapist UI preferences (current user)
  getClientCareNotes, // Added care notes function
  handbackToAI, // Added handback function
  addAnnotation, // Added annotation function
  editAnnotation, // Added edit annotation function
  deleteAnnotation, // Added delete annotation function
  // Onboarding
  sendOnboardingMessage,
  // Goals
  createGoal,
  getGoals,
  updateGoal,
  deleteGoal,
  // Check-Ins
  createCheckIn,
  getCheckIns,
  // Check-In Templates
  getMyCheckInTemplate,
  getClientCheckInTemplate,
  getAllCheckInTemplates,
  submitTemplateCheckIn,
  getTemplateCheckInHistory,
  getCheckInChartData,
  // Treatment Pathway Transitions
  getClientPathway,
  evaluatePhaseReadiness,
  requestPhaseTransition,
  approvePhaseTransition,
  rejectPhaseTransition,
  attestReadinessCriterion,
  getClientsWithPendingTransitions,
  // Session Summaries
  generateSessionSummary,
  getClientSessionSummary,
  getTherapistSessionSummary,

  // Journal API Calls
  createJournalEntry,
  getJournalEntries,
  getJournalEntryById,
  updateJournalEntry, // Add implemented function
  deleteJournalEntry, // Add implemented function

  // Progress Dashboard
  getProgressData,

  // Platform Settings API Calls
  getPlatformSettings,
  updatePlatformSettings,
  getPathwaysList, // Added
  addTreatmentPathway,
  removeTreatmentPathway,
  
  // Client Therapy Settings API Calls
  getClientTherapySettings,
  updateClientTherapySettings,
  
  // Session API Calls
  startSession,
  endSession,
  getSessionHistory,
  getSession,
  updateSession,

  // Chat additions
  requestReview, // Added

  // --- Admin Activity Log API Calls ---
  getAdminActivityLog: async (token, page = 1, limit = 25, filters = {}) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token }, params: { page, limit, ...filters } } : { params: { page, limit, ...filters } };
      const response = await apiClient.get('/admin/activity', config);
      return response.data; // Expects { events, currentPage, totalPages, totalEvents }
    } catch (error) {
      console.error('Get Admin Activity Log API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch admin activity log');
    }
  },

  getTherapistClientActivityLog: async (clientId, token, page = 1, limit = 25, filters = {}) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token }, params: { page, limit, ...filters } } : { params: { page, limit, ...filters } };
      const response = await apiClient.get(`/therapist/activity/client/${clientId}`, config);
      return response.data; // Expects { events, currentPage, totalPages, totalEvents }
    } catch (error) {
      console.error('Get Therapist Client Activity Log API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch client activity log for therapist');
    }
  },

  // --- Logout API Call (for logging) ---
  logout: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      // This is a fire-and-forget call from the client's perspective for logging.
      // The actual logout (token removal) happens client-side regardless of this call's success.
      await apiClient.post('/auth/logout', {}, config); 
      console.log("Logout event sent to server for logging.");
    } catch (error) {
      // Log error but don't throw, as client logout should proceed.
      console.error('Logout API call for logging failed:', error.response?.data || error.message);
    }
  },

  // Therapist Updates Client Summary
  updateClientSummary: async (conversationId, editedClientSummary, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.put(`/therapist/conversations/${conversationId}/client-summary`, { editedClientSummary }, config);
      return response.data; // Expected: { message, clientSummary, originalClientSummary, therapistSummary, reflectionPrompts }
    } catch (error) {
      console.error('Update Client Summary API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to update client summary');
    }
  },

  // User Account functions
  changePassword,
  
  // Utility
  setAuthToken, // Include the utility in the default export if needed by consumers

  // AI Mode Change Settings
  getAiModeChangeSettings,
  updateAiModeChangeSettings,

  // Content Safety Screening Settings
  getContentSafetyScreeningSettings,
  updateContentSafetyScreeningSettings,
  getContentSafetyDefaultKeywords,

  // Therapist Manually Proposing a Goal
  proposeManualTherapistGoal: async (conversationId, goalData, token) => {
    // goalData: { description: string, goalType: 'one_off' | 'habit' }
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/chat/therapist/${conversationId}/propose-manual-goal`, goalData, config);
      return response.data; // Expected: { msg: 'Goal proposal sent to client for confirmation.' }
    } catch (error) {
      console.error('Propose Manual Therapist Goal API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to propose manual goal');
    }
  },

// Get assigned therapist's voice ID
getAssignedTherapistVoice: async (token) => {
  try {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    const response = await apiClient.get('/chat/assigned-therapist-voice', config);
    return response.data; // Returns { voiceId: string | null }
  } catch (error) {
    console.error('Get Assigned Therapist Voice API error:', error.response?.data || error.message);
    throw new Error('Failed to fetch assigned therapist voice');
  }
},


  // Therapist handling assessment suggestion (forward or reject)
  handleTherapistAssessmentAction: async (conversationId, payload, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/chat/therapist/handle-assessment/${conversationId}`, payload, config);
      return response.data;
    } catch (error) {
      console.error('Handle Therapist Assessment Action API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to handle assessment action');
    }
  },

  // --- Assessment API Calls ---
  listTestDefinitions: async (token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.get('/assessments/definitions', config);
  },
  getTestDefinitionDetails: async (testKey, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.get(`/assessments/definitions/${testKey}`, config);
  },
  submitAssessment: async (submissionData, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.post('/assessments/submit', submissionData, config);
  },
  // Admin Assessment Definition Management
  adminListTestDefinitions: async (token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.get('/assessments/admin/definitions', config);
  },
  adminCreateTestDefinition: async (definitionData, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.post('/assessments/admin/definitions', definitionData, config);
  },
  adminUpdateTestDefinition: async (id, definitionData, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.put(`/assessments/admin/definitions/${id}`, definitionData, config);
  },
  adminDeleteTestDefinition: async (id, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.delete(`/assessments/admin/definitions/${id}`, config);
  },
  adminImportTestDefinition: async (formData, token) => {
    // formData is expected to be a FormData object with the file
    const config = token ? { 
      headers: { 
        'x-auth-token': token,
        // 'Content-Type': 'multipart/form-data' // Axios sets this automatically for FormData
      } 
    } : {};
    return apiClient.post('/assessments/admin/definitions/import', formData, config);
  },

  // Therapist initiates assessment for client
  therapistInitiateAssessment: async (conversationId, testKey, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.post(`/therapist/conversation/${conversationId}/initiate-assessment`, { testKey }, config);
  },
  adminGenerateToolForAssessment: async (testKey, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    // No body is sent for this POST request, testKey is in URL
    return apiClient.post(`/assessments/admin/definitions/${testKey}/generate-tool-registry`, {}, config);
  },

  adminExportTestDefinition: async (testKey, token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.get(`/assessments/admin/definitions/${testKey}/export`, config);
  },

  // Admin Tool Registry Management
  adminListTools: async (token) => {
    const config = token ? { headers: { 'x-auth-token': token } } : {};
    return apiClient.get('/tools', config); // Assumes GET /api/tools lists all tools
  },
  // We might need adminCreateTool, adminUpdateTool, adminDeleteTool here later
  // if full tool management from a dedicated UI is built.
  // For now, only list is needed for the "Generate AI Tool" button's conditional display.
  
  // Update AI Response Delay Settings for a Conversation (Therapist)
  updateConversationAiDelaySettings: async (conversationId, delaySettingsData, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.put(`/chat/${conversationId}/ai-delay-settings`, delaySettingsData, config);
      return response.data; // Expected: { msg: '...', aiResponseDelaySettings: {...} }
    } catch (error) {
      console.error('Update Conversation AI Delay Settings API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to update AI response delay settings');
    }
  },

  // Test notification endpoint
  testNotification: async (token, data) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post('/notifications/test', data, config);
      return response.data;
    } catch (error) {
      console.error('Test notification error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to send test notification');
    }
  },

  // ========================================
  // Related Accounts (Parent-Child Linking)
  // ========================================

  // Request to link with another account (e.g., parent linking with child)
  requestAccountLink: async (targetEmail, relationshipType, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post('/account-links/request', { targetEmail, relationshipType }, config);
      return response.data;
    } catch (error) {
      console.error('Request Account Link API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to request account link');
    }
  },

  // Approve a pending account link request
  approveAccountLink: async (linkId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/account-links/${linkId}/approve`, {}, config);
      return response.data;
    } catch (error) {
      console.error('Approve Account Link API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to approve account link');
    }
  },

  // Get all account links for the current user
  getMyAccountLinks: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get('/account-links/my-links', config);
      return response.data;
    } catch (error) {
      console.error('Get My Account Links API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch account links');
    }
  },

  // Get linked children (for parents)
  getLinkedChildren: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get('/account-links/children', config);
      return response.data;
    } catch (error) {
      console.error('Get Linked Children API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch linked children');
    }
  },

  // Get check-in history for a linked child (parent view)
  getChildCheckIns: async (childId, token, options = {}) => {
    try {
      const { limit = 10, offset = 0 } = options;
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get(
        `/account-links/children/${childId}/checkins?limit=${limit}&offset=${offset}`,
        config
      );
      return response.data;
    } catch (error) {
      console.error('Get Child Check-ins API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch child check-ins');
    }
  },

  // Revoke an account link
  revokeAccountLink: async (linkId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.delete(`/account-links/${linkId}`, config);
      return response.data;
    } catch (error) {
      console.error('Revoke Account Link API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to revoke account link');
    }
  },

  // ========================================
  // Parent Check-ins for Children
  // ========================================

  // Get children linked to current user (from check-in routes)
  getMyChildrenForCheckIn: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get('/checkin/my-children', config);
      return response.data;
    } catch (error) {
      console.error('Get My Children API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch children');
    }
  },

  // Submit a check-in for a child (parent submitting)
  submitCheckInForChild: async (childId, checkInData, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/checkin/for-child/${childId}`, checkInData, config);
      return response.data;
    } catch (error) {
      console.error('Submit Check-in for Child API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to submit check-in for child');
    }
  },

  // Get check-ins for a child grouped by informant type
  getChildCheckInsByInformant: async (childId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get(`/checkin/child/${childId}/by-informant`, config);
      return response.data;
    } catch (error) {
      console.error('Get Child Check-ins by Informant API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch child check-ins');
    }
  },

  // ========================================
  // Teacher Invitations (for parents)
  // ========================================

  // Create a teacher invitation for a child
  createTeacherInvitation: async (invitationData, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post('/teacher-portal', invitationData, config);
      return response.data;
    } catch (error) {
      console.error('Create Teacher Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to create teacher invitation');
    }
  },

  // Get teacher invitations for a child
  getTeacherInvitations: async (childId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get(`/teacher-portal/for-child/${childId}`, config);
      return response.data;
    } catch (error) {
      console.error('Get Teacher Invitations API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch teacher invitations');
    }
  },

  // Resend a teacher invitation
  resendTeacherInvitation: async (invitationId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post(`/teacher-portal/${invitationId}/resend`, {}, config);
      return response.data;
    } catch (error) {
      console.error('Resend Teacher Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to resend teacher invitation');
    }
  },

  // Revoke/delete a teacher invitation
  revokeTeacherInvitation: async (invitationId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.delete(`/teacher-portal/${invitationId}`, config);
      return response.data;
    } catch (error) {
      console.error('Revoke Teacher Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to revoke teacher invitation');
    }
  },

  // ========================================
  // Parent-Initiated Adolescent Invitations
  // ========================================

  // Create an adolescent invitation (for parents)
  createAdolescentInvitation: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.post('/parent-invitations/adolescent', {}, config);
      return response.data;
    } catch (error) {
      console.error('Create Adolescent Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to create adolescent invitation');
    }
  },

  // Get all parent-created invitations
  getParentInvitations: async (token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get('/parent-invitations', config);
      return response.data;
    } catch (error) {
      console.error('Get Parent Invitations API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch parent invitations');
    }
  },

  // Get a specific parent invitation
  getParentInvitation: async (invitationId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get(`/parent-invitations/${invitationId}`, config);
      return response.data;
    } catch (error) {
      console.error('Get Parent Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to fetch invitation');
    }
  },

  // Revoke a parent-created invitation
  revokeAdolescentInvitation: async (invitationId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.delete(`/parent-invitations/${invitationId}`, config);
      return response.data;
    } catch (error) {
      console.error('Revoke Adolescent Invitation API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'Failed to revoke invitation');
    }
  },

  // ========================================
  // Teacher Portal (Public - no auth)
  // ========================================

  // Get teacher portal data by token
  getTeacherPortalData: async (token) => {
    try {
      const response = await apiClient.get(`/teacher-portal/portal/${token}`);
      return response.data;
    } catch (error) {
      console.error('Get Teacher Portal Data API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to load teacher portal');
    }
  },

  // Submit teacher consent
  submitTeacherConsent: async (portalToken, consentData) => {
    try {
      const response = await apiClient.post(`/teacher-portal/portal/${portalToken}/consent`, consentData);
      return response.data;
    } catch (error) {
      console.error('Submit Teacher Consent API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to submit consent');
    }
  },

  // Start a teacher assessment
  startTeacherAssessment: async (portalToken, assessmentKey) => {
    try {
      const response = await apiClient.post(`/teacher-portal/portal/${portalToken}/assessment/${assessmentKey}/start`, {});
      return response.data;
    } catch (error) {
      console.error('Start Teacher Assessment API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to start assessment');
    }
  },

  // Submit a teacher assessment
  submitTeacherAssessment: async (portalToken, assessmentKey, responses) => {
    try {
      const response = await apiClient.post(`/teacher-portal/portal/${portalToken}/assessment/${assessmentKey}/submit`, { responses });
      return response.data;
    } catch (error) {
      console.error('Submit Teacher Assessment API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to submit assessment');
    }
  },

  // Submit a teacher check-in
  submitTeacherCheckIn: async (portalToken, checkInData) => {
    try {
      const response = await apiClient.post(`/teacher-portal/portal/${portalToken}/checkin`, checkInData);
      return response.data;
    } catch (error) {
      console.error('Submit Teacher Check-in API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to submit check-in');
    }
  },

  // ========================================
  // Multi-Informant Comparison (Therapist)
  // ========================================

  // Get multi-informant comparison data for a client
  getMultiInformantData: async (clientId, token) => {
    try {
      const config = token ? { headers: { 'x-auth-token': token } } : {};
      const response = await apiClient.get(`/therapist/clients/${clientId}/multi-informant`, config);
      return response.data;
    } catch (error) {
      console.error('Get Multi-Informant Data API error:', error.response?.data || error.message);
      throw new Error(error.response?.data?.msg || 'Failed to fetch multi-informant data');
    }
  },

  // ========================================
  // LLM Configuration (Admin)
  // ========================================
  getLLMConfiguration,
  listLlmConfigs,
  resolveLlmConfig,
  saveLlmConfig,
  deleteLlmConfig,
  updateLLMConfiguration,
  getAvailableLLMModels,
  addAvailableModel,
  updateAvailableModel,
  removeAvailableModel,

  // ========================================
  // Advanced Voice Mode
  // ========================================
  async checkVoiceModeEligibility(token, conversationId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/voice-live/eligibility/${conversationId}`, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      return { eligible: false, reason: error.message };
    }
  },

  async getVoiceModeConfig(token) {
    try {
      const response = await axios.get(`${API_BASE_URL}/voice-live/config`, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async recordVoiceModeConsent(token) {
    try {
      const response = await axios.post(`${API_BASE_URL}/voice-live/consent`, {}, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateTherapistVoiceMode(token, settings) {
    try {
      const response = await axios.put(`${API_BASE_URL}/voice-live/therapist-settings`, settings, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getVoiceCatalogue(token) {
    try {
      const response = await axios.get(`${API_BASE_URL}/voice-live/voices`, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async previewVoice(token, voiceName, sampleText, accentPresetId) {
    try {
      const response = await axios.post(`${API_BASE_URL}/voice-live/preview`,
        { voiceName, sampleText, accentPresetId },
        { headers: { 'x-auth-token': token } }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async getAccentPresets(token) {
    try {
      const response = await axios.get(`${API_BASE_URL}/voice-live/accent-presets`, {
        headers: { 'x-auth-token': token }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default apiClient_default;
